import React, { useState } from 'react';
import {
  Box,
  Text,
  Dropdown,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  AutoComplete,
  SearchIcon,
  ArrowUpRightIcon,
  Link,
  DropdownFooter,
  Button,
  Spinner,
} from '@razorpay/blade/components';
import { statesOfIndia } from 'utils/StatesOfIndia';
import { RegisterGenericType } from 'forms/useRegister';
import { prepareUrlV2 } from 'utils/Urls';
import { statesOfIndiaCodeMap } from '../data';
import { routePaths } from 'components/Routes/data';
import { companyOnboardingSteps } from 'components/Routes/data';
import { useLocation } from 'react-router-dom';
import { useEligiblePtStates } from '../hooks/useEligiblePtStates';

type LwfStateSelectInputProps = {
  registerFormData: RegisterGenericType<{ lwfLocation: string[] }>;
  formData: Partial<{ lwfLocation: string[] }>;
};

export const LwfStateSelectInput = ({ registerFormData, formData }: LwfStateSelectInputProps) => {
  const { data: eligiblePtStatesData, isLoading, isFetching } = useEligiblePtStates();
  const eligibleStates = eligiblePtStatesData?.eligibleStates;

  const [searchState, setSearchState] = useState<string | undefined>(
    formData.lwfLocation?.[0] ? statesOfIndiaCodeMap[Number(formData.lwfLocation[0])] : '',
  );
  const location = useLocation();

  const credentialsUrl = prepareUrlV2(routePaths.phpRoutes.companyOnboarding, {
    step: companyOnboardingSteps.externalCredentials,
    redirect: `${location.pathname}${location.search}`,
  });

  const allEligibleStates = statesOfIndia.filter(({ code }) =>
    (eligibleStates ? eligibleStates : []).includes(code),
  );

  const showStates = allEligibleStates.filter((state) => {
    return searchState ? state.name.toLowerCase().includes(searchState.toLowerCase()) : true;
  });

  return (
    <Dropdown
      onOpenChange={(isOpen) => {
        if (isOpen) {
          setSearchState('');
        } else {
          setSearchState(
            formData.lwfLocation?.[0] ? statesOfIndiaCodeMap[Number(formData.lwfLocation[0])] : '',
          );
        }
      }}>
      <AutoComplete
        label="Location"
        placeholder="Choose Location"
        isRequired
        necessityIndicator="required"
        inputValue={searchState || ''}
        {...registerFormData('lwfLocation', 'select')}
        onInputValueChange={({ value }) => {
          setSearchState(value);
        }}
        filteredValues={showStates.map((state) => state.code.toString())}
      />
      <DropdownOverlay>
        {isLoading || isFetching ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="spacing.5"
            padding="spacing.5">
            <Spinner accessibilityLabel="loading states" />
          </Box>
        ) : showStates.length > 0 ? (
          <>
            <ActionList>
              {showStates.map((state) => (
                <ActionListItem
                  key={state.code}
                  title={state.name}
                  value={state.code.toString()}
                  isSelected={formData.lwfLocation?.[0] === state.code.toString()}
                />
              ))}
            </ActionList>
            <DropdownFooter>
              <Button
                variant="tertiary"
                isFullWidth
                onClick={() => {
                  window.location.href = credentialsUrl;
                }}>
                Add Labour Welfare Fund State
              </Button>
            </DropdownFooter>
          </>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="spacing.5"
            padding="spacing.5">
            <SearchIcon size="xlarge" color="surface.icon.gray.muted" />
            <Box display="flex" flexDirection="column" gap="spacing.3">
              <Text size="medium" weight="semibold" textAlign="center">
                No LWF Locations Found
              </Text>
              <Text
                size="small"
                weight="regular"
                color="surface.text.gray.subtle"
                textAlign="center">
                Please select the applicable Professional Tax states in your company settings to
                enable them here
              </Text>
            </Box>
            <Link href={credentialsUrl} target="_blank" icon={ArrowUpRightIcon} iconPosition="left">
              External Credentials
            </Link>
          </Box>
        )}
      </DropdownOverlay>
    </Dropdown>
  );
};
